// src/app/service/user.service.ts
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { BehaviorSubject } from 'rxjs';
import { HttpsService } from './https.service';
import { SecureStorageService } from './secure-storage.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly browserBackupKey = 'construfacil_account_user';
  private _storage: Storage | null = null;
  private initialized = false;

  // BehaviorSubject para manter o estado reativo do usuário
  private userSubject = new BehaviorSubject<any>(null);
  user$ = this.userSubject.asObservable(); // Observable exposto para os componentes

  constructor(private https: HttpsService, private secureStorage: SecureStorageService) {
    this.init();
  }

  async init() {
    if (!this.initialized) {
      try {
        const storage = new Storage({
          name: 'construfacil',
          storeName: 'user_account',
          driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage]
        });
        this._storage = await storage.create();
      } catch (error) {
        
        this._storage = null;
      }
      this.initialized = true;
    }
  }

  // Carrega o usuário e atualiza o BehaviorSubject
  async loadUser() {
    await this.init();
    let res: any = null;
    try {
      res = await this._storage?.get("account_user");
    } catch (error) {
      
    }
    if (!res && typeof localStorage !== 'undefined') {
      try { res = JSON.parse(localStorage.getItem(this.browserBackupKey) || 'null'); } catch { res = null; }
    }
    if (res && res.length > 0) {
      this.userSubject.next(res[0]); // envia o usuário completo
    } else {
      this.userSubject.next(null);
    }
    return this.userSubject.value;
  }

  // Atualiza os dados do usuário e salva no storage
  async updateUser(data: any) {
    await this.init();
    const currentUser = this.userSubject.value || {};
    const updatedUser = { ...currentUser, ...data };

    // 1) Se vier um user_token, salva no SecureStorage e remove do objeto
    if (updatedUser.user_token) {
      await this.secureStorage.setItem('user_token', updatedUser.user_token);
      delete updatedUser.user_token;
    }

    // 2) Salva o restante dos dados de usuário no storage comum
    try { await this._storage?.set('account_user', [updatedUser]); } catch (error) {
      
    }
    if (typeof localStorage !== 'undefined') localStorage.setItem(this.browserBackupKey, JSON.stringify([updatedUser]));
    this.userSubject.next(updatedUser);
  }

  async logout() {
    await this.init(); // garante que o storage foi inicializado

    try { await this._storage?.remove("account_user"); } catch { /* O fallback será removido abaixo. */ }
    if (typeof localStorage !== 'undefined') localStorage.removeItem(this.browserBackupKey);
    await this.secureStorage.removeItem("user_token");
    this.userSubject.next(null); // atualiza o estado reativo para null
  }

  // Métodos auxiliares para pegar campos específicos
  async getUserId() {
    // 1. Tenta pegar o usuário que já está na memória
    let user = this.userSubject.value;

    // 2. Se a memória estiver vazia (null), força o carregamento do Storage
    if (!user) {
      
      user = await this.loadUser();
    }

    // 3. Agora sim retorna o ID (ou undefined se realmente não estiver logado)
    // Verifique se no seu banco o campo é 'user_id' ou apenas 'id'
    return user?.user_id;
  }

  getUserEmail() {
    return this.userSubject.value?.user_email;
  }

  getUserNome() {
    return this.userSubject.value?.primeiro_nome;
  }

  getUserPaid() {
    return this.userSubject.value?.user_paid;
  }

  getGooglePayments() {
    return this.userSubject.value?.google_payments;
  }

  // Retorna o token do usuário diretamente do SecureStorage
  async getUserToken(): Promise<string | null> {
    return await this.secureStorage.getItem('user_token');
  }

  // Alternativa reativa: pega o valor reativo completo (ex: em componentes)
  get user() {
    return this.userSubject.value;
  }

  // user.service.ts
  async initTrialPeriod() {
    const user_token = await this.secureStorage.getItem("user_token");

    if (!user_token) return;

    const formData = new FormData();
    formData.append("data", JSON.stringify({
      callback: 'init_trial_period'
    }));

    try {
      const res: any = await this.https.post(formData, 'Auth.php');
      if (res.success && res.result) {
        await this.updateUser(res.result[0]);
      }
    } catch (error) {
      
    }
  }

  async syncUserStatus() {
    const currentUser = this.userSubject.value;

    const user_token = await this.secureStorage.getItem("user_token");

    if (!user_token) return;

    const formData = new FormData();
    formData.append("data", JSON.stringify({
      callback: 'get_user_status',
      user_token: user_token
    }));

    try {
      const res: any = await this.https.post(formData, 'Auth.php');
      if (res.success && res.result) {
        await this.updateUser(res.result[0]); // Atualiza dados localmente
      }
    } catch (error) {
      
    }
  }

}
